import type { Command } from './commands';
import {
  BALANCE,
  BUILDING_DEFS,
  CABLE_COST_CENTS,
  JOB_ASSIGN_INTERVAL_TICKS,
  MAP_H,
  MAP_W,
  MAX_DEPTH,
  MAX_STEPS_PER_FRAME,
  PAVED_COST_CENTS,
  RECLAIM_COST_CENTS_PER_LEVEL,
  PIPE_COST_CENTS,
  ROAD_COST_CENTS,
  ROUTE_SALVAGE_FRACTION,
  SPAWN_EDGE,
  STAKE,
  TICKS_PER_DAY,
  TICKS_PER_WEEK,
  TICK_S,
  colOf,
  inBounds,
} from './constants';
import { BoomClock, type BoomClockFrame } from './fixed-step';
import { peepName } from './names';
import { createForks, type RngForks } from './rng';
import {
  FLAG_BUILDING,
  FLAG_CABLE,
  FLAG_PAVED,
  FLAG_PIPE,
  FLAG_POWER,
  FLAG_RAIL,
  FLAG_ROAD,
  GOOD_KINDS,
  ORE_GOLD,
  ORE_IRON,
  type Building,
  type BuildingType,
  type Peep,
  type SimEvent,
  type SimState,
  type Speed,
} from './types';
import {
  dispatchGoodsTruck,
  dispatchTruck,
  goodsCargoValueCents,
  pushLedger,
  stepEconomyDaily,
  runPayroll,
} from './systems/economy';
import { resolveFamilyRequest, stepFamiliesDaily, stepFamiliesWeekly } from './systems/families';
import { corridorCols, rebuildRailFlags } from './systems/rail';
import { stepTiersWeekly, tierWageCents } from './systems/tiers';
import { assignJobs } from './systems/jobs';
import { stepHauling } from './systems/hauling';
import { outputCapUnits, productionStall, stepIndustryDaily } from './systems/industry';
import { stepMarketDaily, stepMarketWeekly } from './systems/market';
import { stepMining } from './systems/mining';
import { stepMovement } from './systems/movement';
import { rebuildPowerNetwork, updateFunctional } from './systems/power';
import { orderWaterRefill, rebuildWaterConnectivity, stepWaterDaily } from './systems/water';
import { resolvePullout, stepEndgameWeekly } from './systems/endgame';
import { stepIncidentsDaily } from './systems/incidents';
import { stepLettersDaily } from './systems/letters';
import { charterReady, charterRequirements, stepImmigrationWeekly, stepTownDaily } from './systems/town';
import { dismissWorkers, stepHiringWeekly, stepNeedsDaily, stepQuitsWeekly } from './systems/workforce';
import { accessTiles, findPath } from './world/path';
import type { HudSnapshot, InspectInfo, PlacementCheck, SimView } from './view';
import { clearFlag, createTerrain, hasFlag, isDiggable, reclaimColumn, setFlag } from './world/terrain';
import { generateTerrain } from './world/terraingen';

const EVENT_CAP = 256;

const startPrice = (meanCents: number) => ({ price: meanCents, mean: meanCents, spikeWeeksLeft: 0 });

export class Simulation implements SimView {
  readonly state: SimState;
  readonly forks: RngForks;
  private readonly clock: BoomClock;
  private readonly queue: Command[] = [];
  private readonly events: SimEvent[] = [];
  networksDirty = true;
  jobsTerrainVersion = -1;
  firstSaleDone = false;
  firstGoodsSaleDone = false;
  firstVeteranSeen = false;
  /** Units of network surplus sellable via interconnects; set on rebuild. */
  powerSellableSurplus = 0;
  kidsGrownUp = 0;
  connectedPipes = new Set<number>();

  constructor(state: SimState, forks: RngForks) {
    this.state = state;
    this.forks = forks;
    this.clock = new BoomClock(TICK_S, MAX_STEPS_PER_FRAME);
  }

  static newGame(seed: number): Simulation {
    const forks = createForks(seed);
    const state: SimState = {
      meta: {
        seed,
        tick: 0,
        speed: 1,
        gameOver: false,
        gameOverReason: null,
        nextEntityId: 1,
        townName: null,
        era: 'company',
        pulloutWeek: null,
        installments: null,
        ending: null,
      },
      terrain: createTerrain(),
      orders: new Map(),
      piles: new Map(),
      goodsPiles: new Map(),
      buildings: [],
      peeps: [],
      households: [],
      familyRequests: [],
      workforce: {
        needs: { housing: 1, food: 1, water: 1, drink: 1, gambling: 1 },
        familyNeeds: { education: 1, health: 1, community: 1, air: 1 },
        unlocked: { drink: false, gambling: false },
        morale: 0.7,
        paidOnTime: 1,
        payrollMissedStreak: 0,
        weeklyQuits: 0,
        weeklyHires: 0,
        hiringOpen: false,
        familyRequestsSent: 0,
      },
      economy: {
        cashCents: BALANCE.startCashCents,
        debtCents: 0,
        bailoutsUsed: 0,
        market: {
          iron: { price: BALANCE.ironPriceMeanCents, mean: BALANCE.ironPriceMeanCents, spikeWeeksLeft: 0 },
          gold: { price: BALANCE.goldPriceMeanCents, mean: BALANCE.goldPriceMeanCents, spikeWeeksLeft: 0 },
          corn: startPrice(BALANCE.commodityMarket.corn.meanCents),
          wheat: startPrice(BALANCE.commodityMarket.wheat.meanCents),
          cattle: startPrice(BALANCE.commodityMarket.cattle.meanCents),
          glass: startPrice(BALANCE.commodityMarket.glass.meanCents),
          chips: startPrice(BALANCE.commodityMarket.chips.meanCents),
          power: startPrice(BALANCE.commodityMarket.power.meanCents),
        },
        depot: { ironDt: 0, goldDt: 0, capacityDt: BALANCE.depotCapacityDt },
        truck: { state: 'idle', returnsOnDay: 0, cargoValueCents: 0 },
        goodsDepot: { units: { corn: 0, wheat: 0, cattle: 0, glass: 0, chips: 0 }, capacityUnits: 0 },
        goodsTruck: { state: 'idle', returnsOnDay: 0, cargoValueCents: 0 },
        marketEvent: null,
        ledger: [],
        weekSeveranceCents: 0,
      },
      water: {
        // Tanks got bigger for town scale, but the camp still starts with the
        // same 500 units in reserve — week-one water pressure is intentional.
        tankLevelUnits: 500,
        tankCapacity: BUILDING_DEFS.waterTank.waterCapacity ?? 1_500,
        autoRefill: true,
        refillArrivesOnDay: null,
        supplyRatio: 1,
      },
      town: {
        attractiveness: 0,
        endureStreak: 0,
        insolventStreak: 0,
        postOreWeeks: 0,
        oreMinedTotalDt: 0,
        quotasCompleted: 0,
        mineIncomeHistory: [],
        townIncomeHistory: [],
        weekMineIncomeCents: 0,
        weekGoodsIncomeCents: 0,
      },
      letters: { delivered: [], quota: null },
      incidents: { lastIncidentDay: -10, log: [] },
      jobTargets: { miners: 4, haulers: 2 },
    };

    generateTerrain(state.terrain, forks.terrain);

    // Starter kit: HQ, ore depot, generator, water tank, trailers, two poles,
    // and a dirt road in from the west edge where the bus and trucks arrive.
    const sim = new Simulation(state, forks);
    sim.placeBuildingRaw('hq', STAKE.x - 1, STAKE.y - 2);
    sim.placeBuildingRaw('oreDepot', STAKE.x + 3, STAKE.y - 2);
    sim.placeBuildingRaw('generator', STAKE.x - 1, STAKE.y + 2);
    sim.placeBuildingRaw('waterTank', STAKE.x + 2, STAKE.y + 2);
    sim.placeBuildingRaw('trailer', STAKE.x - 3, STAKE.y + 1);
    sim.placeBuildingRaw('trailer', STAKE.x - 3, STAKE.y + 2);
    sim.placeBuildingRaw('trailer', STAKE.x - 3, STAKE.y + 3);
    sim.placeBuildingRaw('powerPole', STAKE.x + 3, STAKE.y + 1);
    sim.placeBuildingRaw('powerPole', STAKE.x + 6, STAKE.y);
    for (let x = 0; x < STAKE.x - 3; x += 1) {
      state.terrain.flags[colOf(x, SPAWN_EDGE.y)] = (state.terrain.flags[colOf(x, SPAWN_EDGE.y)] ?? 0) | FLAG_ROAD;
    }

    const spawnSpots: [number, number][] = [
      [STAKE.x - 2, STAKE.y],
      [STAKE.x - 1, STAKE.y],
      [STAKE.x, STAKE.y],
      [STAKE.x + 1, STAKE.y],
      [STAKE.x - 2, STAKE.y + 1],
      [STAKE.x, STAKE.y + 1],
    ];
    for (let index = 0; index < BALANCE.startWorkers; index += 1) {
      const [x, y] = spawnSpots[index % spawnSpots.length]!;
      sim.spawnPeep(x + 0.5, y + 0.5, index < state.jobTargets.miners ? 'miner' : 'hauler');
    }
    return sim;
  }

  spawnPeep(x: number, y: number, job: Peep['job']): Peep {
    const peep: Peep = {
      id: this.state.meta.nextEntityId++,
      name: peepName(this.forks.names),
      job,
      role: 'worker',
      tier: 0,
      tenureWeeks: 0,
      householdId: null,
      growsUpWeek: null,
      familyAskedWeek: null,
      x,
      y,
      px: x,
      py: y,
      path: null,
      pathIdx: 0,
      pathTerrainVersion: 0,
      repathFailures: 0,
      task: { kind: 'idle' },
      carryDt: 0,
      carryType: 0,
      carryGood: null,
    };
    this.state.peeps.push(peep);
    return peep;
  }

  placeBuildingRaw(type: BuildingType, x: number, y: number): Building {
    const def = BUILDING_DEFS[type];
    const building: Building = {
      id: this.state.meta.nextEntityId++,
      type,
      x,
      y,
      staffAssigned: [],
      inputUnits: 0,
      outputUnits: 0,
      outputClaimedUnits: 0,
      outputSuspended: false,
      outageUntilDay: null,
      powered: def.powerDraw === 0,
      powerEff: def.powerDraw === 0 ? 1 : 0,
      watered: true,
      functional: def.staffRequired === 0,
    };
    this.state.buildings.push(building);
    if (type !== 'powerPole') {
      for (let dy = 0; dy < def.h; dy += 1) {
        for (let dx = 0; dx < def.w; dx += 1) {
          if (inBounds(x + dx, y + dy)) setFlag(this.state.terrain, colOf(x + dx, y + dy), FLAG_BUILDING);
        }
      }
    } else {
      setFlag(this.state.terrain, colOf(x, y), 4 /* FLAG_POLE */);
    }
    if (def.corridor) {
      for (const col of rebuildRailFlags(this.state)) this.pushEvent({ kind: 'tileChanged', col });
    }
    this.networksDirty = true;
    this.pushEvent({ kind: 'buildingsChanged' });
    return building;
  }

  enqueue(command: Command): void {
    this.queue.push(command);
  }

  pushEvent(event: SimEvent): void {
    if (this.events.length >= EVENT_CAP) this.events.shift();
    this.events.push(event);
  }

  drainEvents(): SimEvent[] {
    const drained = this.events.splice(0, this.events.length);
    return drained;
  }

  get day(): number {
    return Math.floor(this.state.meta.tick / TICKS_PER_DAY);
  }

  get week(): number {
    return Math.floor(this.state.meta.tick / TICKS_PER_WEEK);
  }

  advance(nowMs: number): BoomClockFrame {
    const speed = this.state.meta.gameOver ? 0 : this.state.meta.speed;
    const frame = this.clock.advance(nowMs, speed, () => this.step());
    // Commands normally apply at the start of a tick, but a paused sim (or a
    // frame too short to produce a step) never ticks — drain here so unpause,
    // building placement, and mail actions still work while time is stopped.
    // Equivalent to applying them at the next tick's start: nothing else
    // mutates sim state in between.
    if (frame.steps === 0 && this.queue.length > 0) {
      const commands = this.queue.splice(0, this.queue.length);
      for (const command of commands) this.applyCommand(command);
    }
    return frame;
  }

  /** Advance exactly one tick — used by tests and the headless balance harness. */
  stepOnce(): void {
    this.step();
  }

  private step(): void {
    const commands = this.queue.splice(0, this.queue.length);
    for (const command of commands) this.applyCommand(command);

    if (this.networksDirty) {
      this.networksDirty = false;
      // Water first: nuclear supply reads `watered`, so the power rebuild
      // must see fresh connectivity. Water has no power dependency.
      rebuildWaterConnectivity(this);
      rebuildPowerNetwork(this);
    }

    this.state.meta.tick += 1;
    const tick = this.state.meta.tick;
    if (tick % JOB_ASSIGN_INTERVAL_TICKS === 0) assignJobs(this);
    stepMovement(this);
    stepMining(this);
    stepHauling(this);
    if (tick % TICKS_PER_DAY === 0) {
      const day = tick / TICKS_PER_DAY;
      if (day % 7 === 0) this.onWeek(day / 7);
      this.onDay(day);
    }
  }

  private applyCommand(command: Command): void {
    this.mutations += 1;
    const state = this.state;
    switch (command.kind) {
      case 'setSpeed':
        state.meta.speed = command.speed;
        break;
      case 'designateDig':
        for (const col of command.cols) {
          if (!isDiggable(state.terrain, col)) continue;
          const existing = state.orders.get(col);
          const depth = state.terrain.depth[col] ?? 0;
          if (existing) {
            // Re-marking a pending order is a no-op; stacking another level
            // takes an explicit deepen (the UI never sends it — players
            // deepen by designating the dug floor again).
            if (command.deepen) existing.targetDepth = Math.min(MAX_DEPTH, existing.targetDepth + 1);
          } else {
            state.orders.set(col, { col, targetDepth: Math.min(MAX_DEPTH, depth + 1), claimedBy: null, suspended: false });
          }
        }
        break;
      case 'cancelDig':
        for (const col of command.cols) {
          const order = state.orders.get(col);
          if (!order) continue;
          if (order.claimedBy !== null) {
            const digger = state.peeps.find((peep) => peep.id === order.claimedBy);
            if (digger && (digger.task.kind === 'toDig' || digger.task.kind === 'digging')) {
              digger.task = { kind: 'idle' };
              digger.path = null;
            }
          }
          state.orders.delete(col);
        }
        break;
      case 'setJobTargets':
        state.jobTargets.miners = Math.max(0, Math.floor(command.miners));
        state.jobTargets.haulers = Math.max(0, Math.floor(command.haulers));
        break;
      case 'dispatchTruck':
        dispatchTruck(this, true);
        break;
      case 'dispatchGoodsTruck':
        dispatchGoodsTruck(this, true);
        break;
      case 'placeBuilding': {
        const check = this.canPlace(command.type, command.x, command.y);
        if (!check.ok) break;
        const def = BUILDING_DEFS[command.type];
        state.economy.cashCents -= def.costCents;
        if (def.costCents > 0) pushLedger(state, this.day, 'build', -def.costCents, `Built ${def.label}`);
        this.placeBuildingRaw(command.type, command.x, command.y);
        break;
      }
      case 'demolish': {
        const index = state.buildings.findIndex((building) => building.id === command.buildingId);
        if (index < 0) break;
        const building = state.buildings[index]!;
        if (building.type === 'hq') break;
        const def = BUILDING_DEFS[building.type];
        for (let dy = 0; dy < def.h; dy += 1) {
          for (let dx = 0; dx < def.w; dx += 1) {
            if (!inBounds(building.x + dx, building.y + dy)) continue;
            const col = colOf(building.x + dx, building.y + dy);
            state.terrain.flags[col] = (state.terrain.flags[col] ?? 0) & ~(FLAG_BUILDING | 4);
          }
        }
        state.terrain.version += 1;
        for (const peep of state.peeps) {
          if (peep.task.kind === 'staffing' && peep.task.buildingId === building.id) {
            peep.task = { kind: 'idle' };
            peep.path = null;
          }
        }
        state.buildings.splice(index, 1);
        if (def.corridor) {
          for (const col of rebuildRailFlags(state)) this.pushEvent({ kind: 'tileChanged', col });
        }
        this.networksDirty = true;
        this.pushEvent({ kind: 'buildingsChanged' });
        break;
      }
      case 'paintRoad': {
        const eligible = [...new Set(command.cols)].filter(
          (col) => !hasFlag(state.terrain, col, FLAG_BUILDING) && !hasFlag(state.terrain, col, FLAG_ROAD),
        );
        if (!this.chargeRoute(eligible.length * ROAD_COST_CENTS, 'that road', 'Road works')) break;
        for (const col of eligible) {
          setFlag(state.terrain, col, FLAG_ROAD);
          this.pushEvent({ kind: 'tileChanged', col });
        }
        break;
      }
      case 'paintPipe': {
        // Pipes run underground, so they may pass beneath buildings — that is
        // how buildings tap the water network.
        const eligible = [...new Set(command.cols)].filter((col) => !hasFlag(state.terrain, col, FLAG_PIPE));
        if (!this.chargeRoute(eligible.length * PIPE_COST_CENTS, 'that pipe run', 'Pipe laying')) break;
        for (const col of eligible) {
          setFlag(state.terrain, col, FLAG_PIPE);
          this.pushEvent({ kind: 'tileChanged', col });
        }
        this.networksDirty = true;
        break;
      }
      case 'paintCable': {
        const eligible = [...new Set(command.cols)].filter((col) => !hasFlag(state.terrain, col, FLAG_CABLE));
        if (!this.chargeRoute(eligible.length * CABLE_COST_CENTS, 'that cable run', 'Buried power cable')) break;
        for (const col of eligible) {
          setFlag(state.terrain, col, FLAG_CABLE);
          this.pushEvent({ kind: 'tileChanged', col });
        }
        this.networksDirty = true;
        break;
      }
      case 'removeRoad': {
        let refund = 0;
        for (const col of command.cols) {
          if (!hasFlag(state.terrain, col, FLAG_ROAD)) continue;
          const paved = hasFlag(state.terrain, col, FLAG_PAVED);
          clearFlag(state.terrain, col, FLAG_ROAD);
          if (paved) clearFlag(state.terrain, col, FLAG_PAVED);
          refund +=
            Math.floor(ROAD_COST_CENTS * ROUTE_SALVAGE_FRACTION) +
            (paved ? Math.floor(PAVED_COST_CENTS * ROUTE_SALVAGE_FRACTION) : 0);
          this.pushEvent({ kind: 'tileChanged', col });
        }
        if (refund > 0) {
          state.economy.cashCents += refund;
          pushLedger(state, this.day, 'build', refund, 'Road salvage');
        }
        break;
      }
      case 'removePipe': {
        let refund = 0;
        for (const col of command.cols) {
          if (!hasFlag(state.terrain, col, FLAG_PIPE)) continue;
          clearFlag(state.terrain, col, FLAG_PIPE);
          refund += Math.floor(PIPE_COST_CENTS * ROUTE_SALVAGE_FRACTION);
          this.pushEvent({ kind: 'tileChanged', col });
        }
        if (refund > 0) {
          state.economy.cashCents += refund;
          pushLedger(state, this.day, 'build', refund, 'Pipe salvage');
          this.networksDirty = true;
        }
        break;
      }
      case 'removeCable': {
        let refund = 0;
        for (const col of command.cols) {
          if (!hasFlag(state.terrain, col, FLAG_CABLE)) continue;
          clearFlag(state.terrain, col, FLAG_CABLE);
          refund += Math.floor(CABLE_COST_CENTS * ROUTE_SALVAGE_FRACTION);
          this.pushEvent({ kind: 'tileChanged', col });
        }
        if (refund > 0) {
          state.economy.cashCents += refund;
          pushLedger(state, this.day, 'build', refund, 'Cable salvage');
          this.networksDirty = true;
        }
        break;
      }
      case 'removePaved': {
        let refund = 0;
        for (const col of command.cols) {
          if (!hasFlag(state.terrain, col, FLAG_PAVED)) continue;
          clearFlag(state.terrain, col, FLAG_PAVED);
          refund += Math.floor(PAVED_COST_CENTS * ROUTE_SALVAGE_FRACTION);
          this.pushEvent({ kind: 'tileChanged', col });
        }
        if (refund > 0) {
          state.economy.cashCents += refund;
          pushLedger(state, this.day, 'build', refund, 'Paving salvage');
        }
        break;
      }
      case 'setAutoRefill':
        state.water.autoRefill = command.enabled;
        break;
      case 'repayDebt': {
        // Manual only — carrying the loan at 5%/wk is a choice, not a leak.
        const amount = Math.min(Math.floor(command.amountCents), state.economy.debtCents, state.economy.cashCents);
        if (amount <= 0) break;
        state.economy.cashCents -= amount;
        state.economy.debtCents -= amount;
        pushLedger(state, this.day, 'interest', -amount, 'Debt repayment');
        if (state.economy.debtCents === 0) {
          this.pushEvent({ kind: 'toast', message: 'Debt cleared. The bookkeeper breathes again.' });
        }
        break;
      }
      case 'orderWaterRefill':
        orderWaterRefill(this);
        break;
      case 'setHiring':
        state.workforce.hiringOpen = command.open;
        break;
      case 'dismissWorkers':
        dismissWorkers(this, command.count);
        break;
      case 'adjustStaff': {
        const building = state.buildings.find((candidate) => candidate.id === command.buildingId);
        if (!building) break;
        const def = BUILDING_DEFS[building.type];
        if (command.delta > 0) {
          if (building.staffAssigned.length >= def.staffRequired) break;
          // Any idle adult staffs first; pulling someone off a mine job is
          // the last resort. Children never staff.
          const recruit =
            state.peeps.find((peep) => peep.role !== 'child' && peep.job === 'idle' && peep.task.kind === 'idle') ??
            state.peeps.find(
              (peep) => peep.role !== 'child' && peep.job !== 'staff' && peep.task.kind === 'idle' && peep.carryDt === 0,
            );
          if (!recruit) {
            // Silence here read as a broken button in playtests.
            this.pushEvent({ kind: 'toast', message: 'No hands free — every adult is busy or hauling.' });
            break;
          }
          building.staffAssigned.push(recruit.id);
          // Industry posts are waged jobs; amenity posts live off takings.
          recruit.job = def.staffWaged ? 'industry' : 'staff';
          recruit.task = { kind: 'staffing', buildingId: building.id };
          const targets = accessTiles(state.terrain, building.x, building.y, def.w, def.h);
          const path = findPath(
            state.terrain,
            colOf(Math.floor(recruit.x), Math.floor(recruit.y)),
            targets,
          );
          recruit.path = path && path.length > 0 ? path : null;
          recruit.pathIdx = 0;
          recruit.pathTerrainVersion = state.terrain.version;
        } else {
          const removed = building.staffAssigned.pop();
          if (removed !== undefined) {
            const peep = state.peeps.find((candidate) => candidate.id === removed);
            if (peep) {
              peep.job = 'idle';
              peep.task = { kind: 'idle' };
              peep.path = null;
            }
          }
        }
        updateFunctional(state);
        // Staffing can change what a supplier feeds the wires (nuclear).
        this.networksDirty = true;
        break;
      }
      case 'markLetterRead': {
        const letter = state.letters.delivered.find((candidate) => candidate.defId === command.defId);
        if (letter) letter.read = true;
        break;
      }
      case 'resolveFamilyRequest':
        resolveFamilyRequest(this, command.requestId, command.approve);
        break;
      case 'signCharter': {
        if (state.meta.townName !== null) break;
        if (!charterReady(this)) break;
        const name = command.name.trim().slice(0, 24);
        if (name.length === 0) break;
        state.meta.townName = name;
        this.pushEvent({ kind: 'incorporated', townName: name });
        this.pushEvent({ kind: 'toast', message: `The town of ${name} is incorporated!` });
        break;
      }
      case 'resolvePullout':
        resolvePullout(this, command.choice);
        break;
      case 'paintPaved': {
        if (state.meta.townName === null) break;
        const eligible = [...new Set(command.cols)].filter(
          (col) => hasFlag(state.terrain, col, FLAG_ROAD) && !hasFlag(state.terrain, col, FLAG_PAVED),
        );
        if (!this.chargeRoute(eligible.length * PAVED_COST_CENTS, 'that paving', 'Street paving')) break;
        for (const col of eligible) {
          setFlag(state.terrain, col, FLAG_PAVED);
          this.pushEvent({ kind: 'tileChanged', col });
        }
        break;
      }
      case 'paintReclaim': {
        // Charter-gated like paving: healing the land is a town project.
        if (state.meta.townName === null) break;
        const eligible: number[] = [];
        let total = 0;
        for (const col of new Set(command.cols)) {
          const depth = state.terrain.depth[col] ?? 0;
          if (depth === 0) continue;
          if (hasFlag(state.terrain, col, FLAG_BUILDING)) continue;
          // Never bury cargo waiting for a hauler.
          if (state.piles.has(col) || state.goodsPiles.has(col)) continue;
          eligible.push(col);
          total += depth * RECLAIM_COST_CENTS_PER_LEVEL;
        }
        if (!this.chargeRoute(total, 'that reclamation', 'Land reclamation')) break;
        for (const col of eligible) {
          const order = state.orders.get(col);
          if (order) {
            if (order.claimedBy !== null) {
              const digger = state.peeps.find((peep) => peep.id === order.claimedBy);
              if (digger && (digger.task.kind === 'toDig' || digger.task.kind === 'digging')) {
                digger.task = { kind: 'idle' };
                digger.path = null;
              }
            }
            state.orders.delete(col);
          }
          reclaimColumn(state.terrain, col);
          this.pushEvent({ kind: 'tileChanged', col });
        }
        break;
      }
      default:
        break;
    }
  }

  /** All-or-nothing route charge: a run either fully builds or fully rejects.
   * Partial commits used to leave truncated, charged-for stubs. Returns true
   * once the money is taken and the ledger entry written. */
  private chargeRoute(totalCents: number, what: string, note: string): boolean {
    if (totalCents <= 0) return false;
    if (this.state.economy.cashCents < totalCents) {
      this.pushEvent({ kind: 'toast', message: `Not enough cash — ${what} needs $${Math.ceil(totalCents / 100)}.` });
      return false;
    }
    this.state.economy.cashCents -= totalCents;
    pushLedger(this.state, this.day, 'build', -totalCents, note);
    return true;
  }

  private onDay(_day: number): void {
    stepMarketDaily(this);
    // Production lands before the trucks roll, so today's harvest can make
    // today's shipment.
    stepIndustryDaily(this);
    stepEconomyDaily(this);
    stepWaterDaily(this);
    stepFamiliesDaily(this);
    stepTownDaily(this);
    stepNeedsDaily(this);
    stepIncidentsDaily(this);
    stepLettersDaily(this);
  }

  private onWeek(_week: number): void {
    stepMarketWeekly(this);
    stepTiersWeekly(this);
    runPayroll(this);
    stepQuitsWeekly(this);
    stepHiringWeekly(this);
    stepFamiliesWeekly(this);
    stepImmigrationWeekly(this);
    stepEndgameWeekly(this);
  }

  // ---- SimView ----

  /** Bumped on every applied command so the hud() memo can't serve stale
   * data for paused-game edits (placing buildings, changing speed...). */
  private mutations = 0;
  private hudMemo: { tick: number; mutations: number; snapshot: HudSnapshot } | null = null;

  hud(): HudSnapshot {
    // The UI reads this many times per frame and every field is O(peeps) or
    // worse — memoize per (tick, mutation) so a 1000-peep town pays once.
    const memo = this.hudMemo;
    if (memo && memo.tick === this.state.meta.tick && memo.mutations === this.mutations) {
      return memo.snapshot;
    }
    const snapshot = this.buildHudSnapshot();
    this.hudMemo = { tick: this.state.meta.tick, mutations: this.mutations, snapshot };
    return snapshot;
  }

  private buildHudSnapshot(): HudSnapshot {
    const state = this.state;
    return {
      cashCents: state.economy.cashCents,
      debtCents: state.economy.debtCents,
      day: this.day,
      week: this.week,
      dayOfWeek: this.day % 7,
      speed: state.meta.speed,
      headcount: state.peeps.length,
      morale: state.workforce.morale,
      depotIronDt: state.economy.depot.ironDt,
      depotGoldDt: state.economy.depot.goldDt,
      depotCapacityDt: state.economy.depot.capacityDt,
      ironPriceCents: state.economy.market.iron.price,
      goldPriceCents: state.economy.market.gold.price,
      waterUnits: state.water.tankLevelUnits,
      waterCapacity: state.water.tankCapacity,
      unreadLetters: state.letters.delivered.filter((letter) => !letter.read).length,
      gameOver: state.meta.gameOver,
      gameOverReason: state.meta.gameOverReason,
      needsHousing: state.workforce.needs.housing,
      needsFood: state.workforce.needs.food,
      needsWater: state.workforce.needs.water,
      needsDrink: state.workforce.needs.drink,
      needsGambling: state.workforce.needs.gambling,
      drinkUnlocked: state.workforce.unlocked.drink,
      gamblingUnlocked: state.workforce.unlocked.gambling,
      hiringOpen: state.workforce.hiringOpen,
      minersTarget: state.jobTargets.miners,
      haulersTarget: state.jobTargets.haulers,
      quota: state.letters.quota
        ? {
            deliveredDt: state.letters.quota.deliveredDt,
            amountDt: state.letters.quota.amountDt,
            byWeek: state.letters.quota.byWeek,
            completed: state.letters.quota.completed,
            failed: state.letters.quota.failed,
          }
        : null,
      townName: state.meta.townName,
      era: state.meta.era,
      attractiveness: state.town.attractiveness,
      endureStreak: state.town.endureStreak,
      charterReady: charterReady(this),
      charterRequirements: state.meta.townName === null ? charterRequirements(this) : [],
      installments: state.meta.installments
        ? {
            paidWeeks: state.meta.installments.paidWeeks,
            totalWeeks: state.meta.installments.totalWeeks,
            weeklyCents: state.meta.installments.weeklyCents,
          }
        : null,
      endingTitle: state.meta.ending?.legacy.title ?? null,
      ...(() => {
        const tierCounts: [number, number, number, number] = [0, 0, 0, 0];
        let kids = 0;
        let onPayroll = 0;
        let industryOnPayroll = 0;
        let dismissible = 0;
        let minersActual = 0;
        let haulersActual = 0;
        let staffCount = 0;
        let projectedWageCents = 0;
        let paidIdle = 0;
        for (const peep of state.peeps) {
          if (peep.role === 'child') kids += 1;
          if (peep.role === 'worker') tierCounts[peep.tier] += 1;
          if (peep.job === 'miner' || peep.job === 'hauler' || peep.job === 'industry') onPayroll += 1;
          if (peep.job === 'industry') industryOnPayroll += 1;
          if (peep.role === 'worker' && peep.householdId === null && peep.job === 'idle') dismissible += 1;
          if (peep.job === 'miner') minersActual += 1;
          if (peep.job === 'hauler') haulersActual += 1;
          if (peep.job === 'staff') staffCount += 1;
          const wage = tierWageCents(peep);
          projectedWageCents += wage;
          if (wage > 0 && peep.task.kind === 'idle') paidIdle += 1;
        }
        return {
          tierCounts,
          kids,
          adults: state.peeps.length - kids,
          onPayroll,
          industryOnPayroll,
          dismissible,
          minersActual,
          haulersActual,
          staffCount,
          projectedWageCents,
          paidIdle,
          employed: onPayroll + staffCount,
        };
      })(),
      goods: GOOD_KINDS.map((good) => ({
        good,
        storedUnits: state.economy.goodsDepot.units[good],
        priceCents: state.economy.market[good].price,
      })),
      goodsCapacityUnits: state.economy.goodsDepot.capacityUnits,
      goodsValueCents: goodsCargoValueCents(state),
      goodsTruckSelling: state.economy.goodsTruck.state === 'selling',
      freightOpen: state.buildings.some(
        (building) => building.type === 'freightOffice' && building.functional,
      ),
      powerSurplusUnits: this.powerSellableSurplus,
      powerPriceCents: state.economy.market.power.price,
      households: state.households.length,
      pendingFamilyRequests: state.familyRequests.filter((request) => request.status === 'pending').length,
      familyEducation: state.workforce.familyNeeds.education,
      familyHealth: state.workforce.familyNeeds.health,
      familyCommunity: state.workforce.familyNeeds.community,
      familyAir: state.workforce.familyNeeds.air,
    };
  }

  canPlace(type: BuildingType, x: number, y: number): PlacementCheck {
    const def = BUILDING_DEFS[type];
    const tiles: boolean[] = [];
    let ok = true;
    let reason: string | null = null;
    for (let dy = 0; dy < def.h; dy += 1) {
      for (let dx = 0; dx < def.w; dx += 1) {
        const tx = x + dx;
        const ty = y + dy;
        let tileOk = inBounds(tx, ty);
        if (tileOk) {
          const col = colOf(tx, ty);
          const depth = this.state.terrain.depth[col] ?? 0;
          tileOk =
            depth === 0 &&
            !hasFlag(this.state.terrain, col, FLAG_BUILDING) &&
            !hasFlag(this.state.terrain, col, FLAG_ROAD) &&
            !hasFlag(this.state.terrain, col, 4 /* FLAG_POLE */) &&
            !hasFlag(this.state.terrain, col, FLAG_RAIL) &&
            !hasFlag(this.state.terrain, col, FLAG_POWER) &&
            !this.state.orders.has(col);
        }
        tiles.push(tileOk);
        if (!tileOk) ok = false;
      }
    }
    if (!ok) reason = 'Blocked or uneven ground';
    // Corridor buildings need their whole right-of-way clear to a map edge.
    let corridor: PlacementCheck['corridor'];
    if (def.corridor) {
      corridor = [];
      const lineName = def.corridor === 'west' ? 'rail line' : 'transmission line';
      const row = y + def.h;
      if (row >= MAP_H) {
        ok = false;
        reason = `Too close to the map edge for the ${lineName}`;
      } else {
        for (const col of corridorCols(type, x, y)) {
          const depth = this.state.terrain.depth[col] ?? 0;
          const colOk =
            depth === 0 &&
            !hasFlag(this.state.terrain, col, FLAG_BUILDING) &&
            !hasFlag(this.state.terrain, col, 4 /* FLAG_POLE */) &&
            !this.state.orders.has(col);
          corridor.push({ col, ok: colOk });
          if (!colOk && ok) {
            ok = false;
            reason = def.corridor === 'west' ? 'Rail corridor blocked' : 'Transmission corridor blocked';
          }
        }
      }
    }
    // The passenger terminal is a platform ON the line: it must touch rail.
    if (type === 'passengerTerminal' && ok) {
      let touchesRail = false;
      for (let dy = -1; dy <= def.h && !touchesRail; dy += 1) {
        for (let dx = -1; dx <= def.w && !touchesRail; dx += 1) {
          if (!inBounds(x + dx, y + dy)) continue;
          if (hasFlag(this.state.terrain, colOf(x + dx, y + dy), FLAG_RAIL)) touchesRail = true;
        }
      }
      if (!touchesRail) {
        ok = false;
        reason = 'Must sit against the rail line';
      }
    }
    if (def.costCents > this.state.economy.cashCents) {
      ok = false;
      reason = 'Not enough cash';
    }
    // Lock reasons win over cash — a locked building should say why it's locked.
    if (def.unlockedBy === 'charter') {
      if (this.state.meta.townName === null) {
        ok = false;
        reason = 'Requires town charter';
      }
    } else if (def.unlockedBy && !this.state.letters.delivered.some((letter) => letter.defId === def.unlockedBy)) {
      ok = false;
      reason = 'Not yet unlocked';
    }
    return { ok, reason, tiles, corridor };
  }

  inspect(x: number, y: number): InspectInfo | null {
    if (!inBounds(x, y)) return null;
    const col = colOf(x, y);
    const building = this.state.buildings.find((candidate) => {
      const def = BUILDING_DEFS[candidate.type];
      return x >= candidate.x && x < candidate.x + def.w && y >= candidate.y && y < candidate.y + def.h;
    });
    if (building) {
      const def = BUILDING_DEFS[building.type];
      const lines = [
        building.functional ? 'Operating' : 'Not operating',
        def.powerDraw > 0 ? (building.powered ? 'Powered' : 'NO POWER') : '',
        def.staffRequired > 0 ? `Staff ${building.staffAssigned.length}/${def.staffRequired}` : '',
      ].filter(Boolean);
      if (def.service && def.serviceCap) {
        const demand =
          def.service === 'education'
            ? this.state.peeps.filter((peep) => peep.role === 'child').length
            : this.state.peeps.length;
        lines.push(`Serves ${Math.min(demand, def.serviceCap)}/${def.serviceCap} (${def.service})`);
      }
      if (def.produces) {
        const stall = productionStall(this.state, building);
        lines.push(stall === null ? `Producing ${def.produces.unitsPerDay} ${def.produces.good}/day` : `Halted: ${stall}`);
        lines.push(`Store ${building.outputUnits}/${outputCapUnits(building.type)} ${def.produces.good}`);
      }
      if (def.consumes) {
        lines.push(`Feed ${building.inputUnits}/${def.consumes.bufferUnits} ${def.consumes.good}`);
      }
      if (def.goodsCapacityUnits) {
        lines.push(`Adds ${def.goodsCapacityUnits} goods storage`);
      }
      if ((def.familyHomes ?? 0) > 0) {
        const households = this.state.households.filter((candidate) => candidate.homeBuildingId === building.id);
        lines.push(`${households.length}/${def.familyHomes} homes occupied`);
        for (const household of households.slice(0, 3)) {
          const head = this.state.peeps.find(
            (peep) => peep.id === (household.workerId ?? household.spouseId),
          );
          const surname = head?.name.split(' ').slice(-1)[0] ?? '?';
          lines.push(`The ${surname}s · ${Math.round(household.happiness * 100)}%`);
        }
        const avgDust = households.reduce((sum, h) => sum + h.dust, 0) / Math.max(1, households.length);
        if (avgDust > 0.3) lines.push(`Dusty location (${Math.round(avgDust * 100)}%)`);
      }
      return { kind: 'building', title: def.label, lines, buildingId: building.id };
    }
    const depth = this.state.terrain.depth[col] ?? 0;
    const cell = depth < MAX_DEPTH ? depth * MAP_W * MAP_H + col : -1;
    const oreType = cell >= 0 ? (this.state.terrain.oreType[cell] ?? 0) : 0;
    const oreAmount = cell >= 0 ? (this.state.terrain.oreAmount[cell] ?? 0) : 0;
    const lines = [`Level ${depth === 0 ? 'surface' : `-${depth}`}`];
    if (oreType === ORE_IRON) lines.push(`Iron below: ~${(oreAmount / 10).toFixed(1)} t`);
    if (oreType === ORE_GOLD) lines.push(`Gold below: ~${(oreAmount / 10).toFixed(1)} t`);
    if (this.state.terrain.reclaimed[col]) lines.push('Reclaimed ground');
    if (this.state.orders.has(col)) lines.push('Marked for digging');
    if (hasFlag(this.state.terrain, col, FLAG_ROAD)) lines.push('Road');
    return { kind: 'tile', title: `Tile ${x}, ${y}`, lines };
  }
}
